import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Facebook, Instagram, Music2, Loader2, Copy, Sparkles, ExternalLink,
  Link2, RefreshCw, BadgeDollarSign,
} from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../ui/button";

const NETWORKS = [
  {
    id: "facebook", label: "Facebook", icon: Facebook,
    active: "border-blue-500 bg-blue-600/15 text-white", chip: "text-blue-400",
    open: "https://www.facebook.com", openLabel: "Abrir Facebook",
    mediaTitle: "¿Qué imagen o video usar en Facebook?",
    media: [
      "Sirve una imagen o un video (ambos funcionan muy bien).",
      "Imagen: cuadrada o vertical, la que descargaste de tu campaña.",
      "Video: ideal de menos de 1 minuto.",
      "En Facebook tu enlace SÍ es clickeable: ya va incluido en tu texto adaptado.",
    ],
    steps: [
      "Abre Facebook (botón de abajo o la app en tu celular).",
      "Arriba dice '¿Qué estás pensando?'. Toca ahí.",
      "Toca 'Foto/Video' y elige la imagen o el video que descargaste en el Paso 1.",
      "Pega el texto adaptado (ya incluye tu enlace).",
      "Toca 'Publicar'. Comparte también en 1 o 2 grupos relacionados (no más, para evitar spam).",
      "Responde rápido los comentarios e invita por mensaje privado a los interesados.",
    ],
  },
  {
    id: "instagram", label: "Instagram", icon: Instagram,
    active: "border-pink-500 bg-pink-600/15 text-white", chip: "text-pink-400",
    open: "https://www.instagram.com", openLabel: "Abrir Instagram",
    mediaTitle: "¿Qué imagen o video usar en Instagram?",
    media: [
      "Publicación (feed): imagen cuadrada o vertical.",
      "Reel: video VERTICAL 9:16, ideal de 15 a 60 segundos.",
      "Formato MP4 para video y JPG para imagen (así se descargan de tu campaña).",
      "El enlace NO es clickeable en la descripción: ponlo en tu BIO (perfil).",
    ],
    steps: [
      "Primero pon tu enlace en tu bio: Perfil → 'Editar perfil' → 'Sitio web' → pega tu enlace.",
      "Abre Instagram y toca el botón +.",
      "Elige 'Publicación' (imagen) o 'Reel' (video) y selecciona el archivo que descargaste.",
      "Toca 'Siguiente' y pega el texto adaptado en la descripción.",
      "Toca 'Compartir'.",
      "Responde los comentarios con: 'te dejo la info en el enlace de mi bio 👆'.",
    ],
  },
  {
    id: "tiktok", label: "TikTok", icon: Music2,
    active: "border-cyan-400 bg-cyan-500/15 text-white", chip: "text-cyan-300",
    open: "https://www.tiktok.com/upload", openLabel: "Abrir TikTok",
    mediaTitle: "Video recomendado para TikTok",
    media: [
      "Video VERTICAL 9:16 (que llene la pantalla del celular).",
      "Duración ideal: 15 a 34 segundos (máximo 60).",
      "Formato MP4 — así se descarga de tu campaña. ✅",
      "El gancho (lo más llamativo) va en los primeros 3 segundos.",
      "El enlace NO es clickeable en la descripción: colócalo en tu PERFIL (biografía).",
    ],
    steps: [
      "Primero pon tu enlace en tu perfil: Perfil → 'Editar perfil' → 'Biografía' o 'Sitio web' → pega tu enlace.",
      "Descarga el video de tu campaña a tu celular (Paso 1).",
      "Abre TikTok y toca el botón + del centro.",
      "Toca 'Subir' (junto al botón de grabar) y elige el video de tu campaña.",
      "Pega la DESCRIPCIÓN que te preparó la IA (corta y con hashtags). Si prefieres grabarte tú, usa el GUION del video.",
      "Toca 'Publicar'. Responde los comentarios con: 'la info está en el enlace de mi perfil 👆'.",
    ],
  },
];

export default function NetworkGuide({ campaign, link, Step }) {
  const [net, setNet] = useState(null);
  const [texts, setTexts] = useState({});
  const [loading, setLoading] = useState(false);
  const current = NETWORKS.find((n) => n.id === net);
  const adapted = texts[net];

  const adapt = async (regenerate) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/guided/campaigns/${campaign.gc_id}/adapt-text`, {
        platform: net, link: link || "", regenerate,
      });
      setTexts((t) => ({ ...t, [net]: data.text }));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setLoading(false);
  };

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("¡Texto copiado!"); };

  return (
    <>
      <Step n={2} title="📍 ¿Dónde vas a publicar?" desc="Cada red social funciona diferente. Elige una y te damos las indicaciones exactas.">
        <div className="grid grid-cols-3 gap-3">
          {NETWORKS.map((n) => (
            <button key={n.id} data-testid={`net-${n.id}`} onClick={() => setNet(n.id)}
              className={`flex flex-col items-center gap-2 rounded-xl border py-4 transition-colors ${net === n.id ? n.active : "border-white/10 bg-[#121212] text-zinc-400 hover:border-purple-500/50"}`}>
              <n.icon size={22} className={net === n.id ? n.chip : "text-zinc-500"} />
              <span className="text-sm font-semibold">{n.label}</span>
            </button>
          ))}
        </div>
        {current && (
          <div data-testid="media-specs" className="mt-4 rounded-xl border border-yellow-400/30 bg-yellow-400/5 p-4">
            <p className="text-sm font-bold text-yellow-impact mb-2">{current.mediaTitle}</p>
            <ul className="space-y-1.5">
              {current.media.map((m) => (
                <li key={m} className="text-sm text-zinc-300 flex items-start gap-2"><span className="text-yellow-400 mt-0.5">•</span>{m}</li>
              ))}
            </ul>
          </div>
        )}
      </Step>

      {current && (
        <Step n={3} title={`✍️ Tu texto adaptado para ${current.label}`} desc="La IA reescribe el texto de tu campaña con el formato exacto que funciona en esta red. Es gratis, ya está incluido en tu campaña.">
          {!link && (
            <p className="text-xs text-yellow-impact mb-3 flex items-center gap-1.5">
              <Link2 size={13} /> Conecta arriba tu embudo o página para que tu enlace se incluya automáticamente.
            </p>
          )}
          {!adapted ? (
            <Button data-testid="adapt-text" onClick={() => adapt(false)} disabled={loading} className="bg-purple-600 hover:bg-purple-500 rounded-xl">
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Sparkles size={16} className="mr-2" />} Adaptar mi texto para {current.label}
            </Button>
          ) : (
            <>
              <div data-testid="adapted-text" className="rounded-xl bg-black border border-white/10 p-4 mb-3">
                <p className="text-sm text-zinc-200 whitespace-pre-wrap">{adapted}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button data-testid="copy-adapted" onClick={() => copy(adapted)} className="bg-purple-600 hover:bg-purple-500 rounded-xl">
                  <Copy size={16} className="mr-2" /> Copiar texto
                </Button>
                <Button data-testid="regen-adapted" onClick={() => adapt(true)} disabled={loading} variant="outline" className="border-white/15 bg-transparent hover:bg-white/5 rounded-xl">
                  {loading ? <Loader2 className="animate-spin mr-2" size={15} /> : <RefreshCw size={15} className="mr-2" />} Regenerar
                </Button>
              </div>
            </>
          )}
        </Step>
      )}

      {current && (
        <Step n={4} title={`🚀 Publica en ${current.label}, paso a paso`} desc="Sigue los pasos en orden. Publicar así es totalmente gratis.">
          <ol className="space-y-2 mb-4">
            {current.steps.map((s, i) => (
              <li key={s} className="flex items-start gap-3 text-sm text-zinc-300">
                <span className="w-6 h-6 rounded-full bg-green-600/20 border border-green-500/40 text-green-300 flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
          <a data-testid={`open-${current.id}`} href={current.open} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white font-bold rounded-xl px-5 py-3 text-sm">
            <ExternalLink size={16} /> {current.openLabel}
          </a>
          <div className="mt-4 rounded-xl border border-white/10 bg-black/40 p-3 flex items-start gap-2">
            <BadgeDollarSign size={16} className="text-yellow-400 mt-0.5 shrink-0" />
            <p className="text-xs text-zinc-400">
              Publicar en tu perfil es <strong className="text-green-400">gratis</strong>. Si quieres <strong className="text-zinc-200">invertir en publicidad pagada</strong> para
              llegar a personas que no te conocen, te enseñamos paso a paso en{" "}
              <Link to="/dashboard/publicaciones-gratis?tab=ads" data-testid="goto-ads-guide" className="text-yellow-impact underline">Publicidad con costo</Link>.
            </p>
          </div>
        </Step>
      )}
    </>
  );
}
